import { useMemo, useRef, useState } from 'react'
import { AlertTriangle, ArrowRight, Check, FileSpreadsheet, Upload } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FixedTable, type FixedColumn } from '@/components/ui/FixedTable'
import { Pill } from '@/components/ui/StatusPill'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { ProposalPricingImportService as Service, type ImportPreview } from '@/services/modules/ProposalPricingService'

const money = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/**
 * Importar a planilha "Simulador" como nova versão da tabela de preço.
 *
 * 1) lê e compara com a versão ativa; 2) o usuário confere e decide o que é
 * renomeação, item novo, variação de grafia…; 3) salva como rascunho ou já
 * ativa. Nada muda antes do passo 3, e propostas já criadas nunca mudam.
 */
export function PricingImportModal({ open, canActivate, onClose, onDone }: {
  open: boolean
  canActivate: boolean
  onClose: () => void
  onDone: (tableId: string) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [reading, setReading] = useState(false)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [decisions, setDecisions] = useState<Record<string, string>>({})
  const [name, setName] = useState('')
  const [saving, setSaving] = useState<'draft' | 'active' | null>(null)
  const [onlyChanged, setOnlyChanged] = useState(true)
  const input = useRef<HTMLInputElement>(null)

  function reset() {
    setFile(null); setPreview(null); setDecisions({}); setName(''); setSaving(null); setOnlyChanged(true)
  }
  function close() { reset(); onClose() }

  async function read() {
    if (!file) return
    setReading(true)
    try {
      const p = await Service.preview(file)
      setPreview(p)
      setDecisions(Object.fromEntries(p.decisions.map(d => [d.key, d.suggested])))
      const hoje = new Date().toLocaleDateString('pt-BR')
      setName(`Planilha de ${hoje}`)
    } catch (e) {
      toast.error({ title: 'Não foi possível ler a planilha', description: extractApiError(e).message })
    } finally { setReading(false) }
  }

  async function apply(activate: boolean) {
    if (!preview || !name.trim()) return
    setSaving(activate ? 'active' : 'draft')
    try {
      const t = await Service.apply({ token: preview.token, name: name.trim(), decisions, activate })
      toast.success({
        title: activate ? 'Nova tabela no ar' : 'Rascunho criado',
        description: activate ? `A versão ${t.version} já vale para as próximas propostas.` : `Versão ${t.version}. Confira e ative quando quiser.`,
      })
      reset()
      onDone(t.id)
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
      setSaving(null)
    }
  }

  const prices = useMemo(() => {
    const all = preview?.prices ?? []
    return onlyChanged ? all.filter(p => p.current === null || Math.abs((p.current ?? 0) - p.next) >= 0.005 || p.renamedFrom) : all
  }, [preview, onlyChanged])

  const footer = !preview ? (
    <>
      <Button variant="ghost" onClick={close} disabled={reading}>Cancelar</Button>
      <Button onClick={read} disabled={!file} loading={reading} iconRight={<ArrowRight size={14} />}>Ler e conferir</Button>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={reset} disabled={!!saving}>Trocar arquivo</Button>
      <Button variant="secondary" onClick={() => apply(false)} loading={saving === 'draft'} disabled={!!saving || !name.trim()}>Salvar como rascunho</Button>
      {canActivate && <Button onClick={() => apply(true)} loading={saving === 'active'} disabled={!!saving || !name.trim()}>Salvar e ativar</Button>}
    </>
  )

  type PriceRow = ImportPreview['prices'][number]
  const priceCols: FixedColumn<PriceRow>[] = [
    { key: 'line', header: 'Linha', width: 140, render: p => p.line },
    { key: 'tier', header: 'Modalidade', render: p => (
      <div className="py-0.5">
        <p className="font-medium text-gray-900">{p.tier}</p>
        {p.renamedFrom && <p className="text-xs text-gray-500">era {p.renamedFrom}</p>}
      </div>
    ) },
    { key: 'current', header: 'Atual', width: 140, numeric: true, render: p => p.current !== null ? <span className="text-gray-600">{money(p.current)}</span> : <Pill tone="blue" dot={false}>Nova</Pill> },
    { key: 'next', header: 'Planilha', width: 140, numeric: true, render: p => <span className="font-semibold">{money(p.next)}</span> },
    { key: 'diff', header: 'Variação', width: 120, numeric: true, render: p => {
      const diff = p.current ? (p.next - p.current) / p.current : null
      if (diff === null) return <span className="text-gray-400">—</span>
      const zero = Math.abs(diff) < 0.00005
      return <span className={zero ? 'text-gray-400' : diff > 0 ? 'text-rose-700' : 'text-emerald-700'}>
        {`${diff > 0 && !zero ? '+' : ''}${(diff * 100).toFixed(2).replace('.', ',').replace('-', '−')}%`}
      </span>
    } },
  ]

  const steps = [
    { label: preview ? 'Arquivo lido' : 'Escolher arquivo', state: preview ? 'done' : 'current' },
    { label: 'Conferir', state: preview ? 'current' : 'todo' },
    { label: 'Salvar', state: 'todo' },
  ] as const

  return (
    <Modal open={open} onClose={close} size="xl" title="Importar planilha de preços" footer={footer}
      description={preview
        ? `${preview.fileName} · comparado com ${preview.activeVersion ? `a versão ativa (v${preview.activeVersion.version})` : 'nada — não há versão ativa'}. Nada muda antes de salvar.`
        : 'Envie a planilha do simulador (abas “<Linha> Database”). Nada muda antes de você conferir e salvar.'}>
      <ol aria-label="Etapas" className="-mx-1 mb-5 flex flex-wrap gap-x-6 gap-y-2">
        {steps.map((s, i) => (
          <li key={s.label} aria-current={s.state === 'current' ? 'step' : undefined}
            className={`flex items-center gap-2 text-[13px] ${s.state === 'done' ? 'font-medium text-emerald-700' : s.state === 'current' ? 'font-semibold text-brand-700' : 'font-medium text-gray-500'}`}>
            <span className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs ${s.state === 'done' ? 'bg-emerald-500 text-white' : s.state === 'current' ? 'bg-brand-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
              {s.state === 'done' ? <Check size={12} strokeWidth={3} /> : i + 1}
            </span>
            {s.label}
          </li>
        ))}
      </ol>

      {!preview ? (
        <div>
          <input ref={input} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={e => setFile(e.target.files?.[0] ?? null)} />
          <button type="button" onClick={() => input.current?.click()}
            className="flex w-full cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center transition hover:border-brand-400 hover:bg-brand-50/40">
            {file ? <FileSpreadsheet size={28} className="text-emerald-600" /> : <Upload size={28} className="text-gray-400" />}
            <span className="break-words text-sm font-semibold text-gray-800">{file ? file.name : 'Escolher arquivo .xlsx'}</span>
            <span className="text-xs text-gray-500">{file ? `${(file.size / 1024).toFixed(0)} KB · clique para trocar` : 'Até 20 MB'}</span>
          </button>
          <ul className="mt-4 space-y-1 text-[13px] text-gray-600">
            <li>• Preço cheio, pesos e faixas de desconto vêm de cada aba “Database”.</li>
            <li>• Nomes que mudaram são reconhecidos pelo preço ou pelos pesos, e você decide se é renomeação.</li>
            <li>• Linhas que não estão na planilha mantêm os valores atuais.</li>
          </ul>
        </div>
      ) : (
        <div className="space-y-5">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {([
              ['Linhas', preview.summary.lines, false],
              ['Modalidades', preview.summary.tiers, false],
              ['Preços alterados', preview.summary.priceChanges, false],
              ['Pesos alterados', preview.summary.weightChanges, false],
              ['Para decidir', preview.summary.decisions, preview.summary.decisions > 0],
            ] as const).map(([k, v, warn]) => (
              <div key={k} className={`rounded-lg px-3.5 py-2.5 ring-1 ${warn ? 'bg-amber-50 ring-amber-300' : 'bg-white ring-gray-200'}`}>
                <dt className={`text-xs ${warn ? 'text-amber-800' : 'text-gray-500'}`}>{k}</dt>
                <dd className={`mt-0.5 text-xl font-semibold tabular-nums ${warn ? 'text-amber-800' : 'text-gray-900'}`}>{v}</dd>
              </div>
            ))}
          </dl>

          {preview.warnings.length > 0 && (
            <div className="flex gap-2.5 rounded-lg bg-amber-50 px-3.5 py-3 text-[13px] text-amber-900 ring-1 ring-amber-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <ul className="space-y-0.5">{preview.warnings.map(w => <li key={w}>{w}</li>)}</ul>
            </div>
          )}

          {preview.decisions.length > 0 && (
            <div className="space-y-2.5">
              <div>
                <h3 className="text-[15px] font-semibold text-gray-900">Confira antes de salvar</h3>
                <p className="text-[13px] text-gray-600">A opção mais provável já vem marcada. Renomear guarda o nome antigo: propostas já salvas continuam com ele.</p>
              </div>
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                {preview.decisions.map(d => (
                  <fieldset key={d.key} className="space-y-2 rounded-lg px-3.5 pb-3 pt-1 ring-1 ring-gray-200">
                    <legend className="px-1 text-[13px] font-semibold text-gray-900">{d.title}</legend>
                    {d.detail && <p className="text-xs text-gray-500">{d.detail}</p>}
                    {d.options.map(o => (
                      <label key={o.action} className={`flex cursor-pointer items-start gap-2.5 text-sm leading-snug ${o.secondary ? 'text-gray-500' : 'text-gray-800'}`}>
                        <input type="radio" name={d.key} value={o.action} checked={decisions[d.key] === o.action}
                          onChange={() => setDecisions(s => ({ ...s, [d.key]: o.action }))} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600" />
                        <span>{o.label}{o.action === d.suggested && <span className="ml-1.5 rounded bg-brand-50 px-1.5 py-0.5 text-[11px] font-semibold text-brand-700">sugerido</span>}</span>
                      </label>
                    ))}
                  </fieldset>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-[15px] font-semibold text-gray-900">Preço cheio por modalidade</h3>
              <label className="flex cursor-pointer items-center gap-2 text-[13px] text-gray-600">
                <input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} className="h-4 w-4 accent-brand-600" />
                Só o que muda
              </label>
            </div>
            <div className="overflow-hidden rounded-lg ring-1 ring-gray-200">
              <FixedTable columns={priceCols} rows={prices} getRowKey={p => `${p.line}|${p.tier}`} minWidth={640}
                emptyMessage="Nenhum preço cheio muda. Os pesos e faixas podem ter mudado — veja o resumo acima." />
            </div>
          </div>

          <Input label="Nome da nova versão" value={name} onChange={e => setName(e.target.value)} maxLength={120}
            hint={canActivate
              ? '“Salvar e ativar” coloca no ar na hora para as próximas propostas. Propostas já criadas não mudam.'
              : 'A versão fica como rascunho; um administrador ativa depois.'} />
        </div>
      )}
    </Modal>
  )
}
